import React, { useCallback, useRef } from 'react';
import CreateShareableLinkContext from './CreateShareableLink.context';
import {
  CreateDataRoomShareableLinkTemplate,
  DataRoomShareableLink,
} from 'models/ShareableLink';
import ShareableLinkDrawer from '../../components/DataRoomDocumentsRouter/components/ShareableLinkDrawer';
import useShareableLink from '../../hooks/useShareableLink';

type CreateShareableLinkProps = {
  onSuccessFullyCreated?: (link: DataRoomShareableLink) => void;
};

const CreateShareableLink: React.FC<CreateShareableLinkProps> = (props) => {
  const { children, onSuccessFullyCreated } = props;

  const {
    drawer,
    selectedLink,
    selectorNodes,
    isLoading,
    checkedFileIds,
    checkedFolderIds,
    initiallyCheckedNodes,
    setCheckedFileIds,
    setCheckedFolderIds,
    setSelectorNodes,
    drawerRef,
    setSelectedLink,
    openDrawer,
    onCloseDrawer,
  } = useShareableLink();

  // A ref rather than state: what the drawer shows its back button on is the
  // template's own `waitingRoomRequestId`, so nothing has to re-render for this.
  // Every opening sets it, so a way back never survives into the next creation.
  const onBackRef = useRef<() => void>();

  const resetChecked = useCallback(() => {
    setCheckedFileIds([]);
    setCheckedFolderIds([]);
  }, [setCheckedFileIds, setCheckedFolderIds]);

  const openCreateShareableLink = useCallback(
    (fileIds: string[] = []) => {
      resetChecked();
      onBackRef.current = undefined;
      openDrawer(fileIds);
    },
    [openDrawer, resetChecked],
  );

  const openCreateShareableLinkWithTemplate = useCallback(
    (link: CreateDataRoomShareableLinkTemplate, onBack?: () => void) => {
      resetChecked();
      setSelectedLink(link);
      onBackRef.current = onBack;
      openDrawer(link.fileIds, link.folderIds);
    },
    [openDrawer, resetChecked, setSelectedLink],
  );

  // Going back is leaving the drawer, so it closes here — the caller only has to
  // reopen what it handed off from.
  const onBack = useCallback(() => {
    drawer?.setOpen(false);
    onBackRef.current?.();
  }, [drawer]);

  return (
    <CreateShareableLinkContext.Provider
      value={{ openCreateShareableLink, openCreateShareableLinkWithTemplate }}
    >
      {children}

      <ShareableLinkDrawer
        ref={drawerRef}
        nodes={selectorNodes}
        setNodes={setSelectorNodes}
        isLoadingNodes={isLoading}
        initiallyCheckedNodes={initiallyCheckedNodes}
        checkedFileIds={checkedFileIds}
        checkedFolderIds={checkedFolderIds}
        link={selectedLink}
        onBack={onBack}
        onSuccessLink={onSuccessFullyCreated}
        closeAction={onCloseDrawer}
      />
    </CreateShareableLinkContext.Provider>
  );
};

export default CreateShareableLink;
